import logo from './logo.svg';
import './App.css';
import React, { useState, useEffect } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { LargeUpload } from './components/LargeFileUpload/LargeUpload.tsx';
import { Stack, IStackStyles, IStackTokens, IStackItemStyles } from '@fluentui/react/lib/Stack';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import {
    Calendar,
    defaultCalendarStrings,
    MessageBar,
    MessageBarType,
    MessageBarButton,
} from '@fluentui/react';
import {
    fluentTooltip,
    provideFluentDesignSystem,
    fluentAccordion,
    fluentAccordionItem
} from "@fluentui/web-components";

const REQUIRED_ROLE = process.env.REACT_APP_ROLE_REQUIRED;

// Global scopes variable
const MSAL_SCOPES = ["User.Read", "Sites.ReadWrite.All" ]

function userHasRequiredRole(account) {
    if (!account || !account.idTokenClaims) return false;
    const roles = account.idTokenClaims.roles || account.idTokenClaims['roles'] || [];
    return roles.includes(REQUIRED_ROLE);
}


function GenerateExpiryDate() {
    const daysToAdd = Number(process.env.REACT_APP_DEFAULT_EXPIRY_DAYS) || 30;
    const expd = new Date();
    expd.setDate(expd.getDate() + daysToAdd);
    return expd;
}

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function App() {
    const largeUploadRef = React.useRef(null);
    const { instance, accounts, inProgress } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [accessToken, setAccessToken] = useState(null);
    const [inviteeEmail, setInviteeEmail] = useState(""); // State for Invitee email
    const [referenceCode, setReferenceCode] = useState(""); // State for referenceCode
    const [lastInvitee, setLastInvitee] = useState(""); // New state for last invitee
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteeEmail);
    const [isSharing, setIsSharing] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // Define shareEndpoint from environment variable
    const shareEndpoint = process.env.REACT_APP_SHARE_ENDPOINT;

    provideFluentDesignSystem()
        .register(
            fluentTooltip()
        );

    const SuccessExample = () => (
        <MessageBar 
            actions={
                <div>
                    <MessageBarButton onClick={() => setShowSuccess(false)}>Close</MessageBarButton>
                </div>
            }
            messageBarType={MessageBarType.success}
            isMultiline={false}
        >
            File has been shared with {lastInvitee}.
        </MessageBar>
    );

    provideFluentDesignSystem()
        .register(
            fluentAccordion(),
            fluentAccordionItem()
        );
  
    useEffect(() => {
        if (account) {
            instance.acquireTokenSilent({
                scopes: MSAL_SCOPES,
                account: account
            }).then((response) => {
                if (response && response.accessToken) {
                    setAccessToken(response.accessToken);
                   
                }
            }).catch(() => {
                // Fallback to interactive if silent fails
                instance.acquireTokenPopup({
                    scopes: MSAL_SCOPES,
                    account: account
                }).then((response) => {
                    if (response && response.accessToken) {
                        setAccessToken(response.accessToken);
                    }
                });
            });
        }
    }, [account, instance]);

    // If not signed in, prompt login
    const handleLogin = () => {
        instance.loginPopup({ scopes: MSAL_SCOPES });
    };

    // Share button handler
    const onShareClicked = async () => {
        if (isSharing) return; // Prevent re-entry
        setIsSharing(true);

        await delay(3000); // Add 3 second delay

        const payload = {
            SPSiteDisplayName: process.env.REACT_APP_SP_SITE_DISPLAY_NAME,
            InviteeEmail: inviteeEmail,
            SPLibraryName: process.env.REACT_APP_SP_LIBRARY_NAME,
            FileNameForShare: largeUploadRef.current?.sharepointFilename,
            ExpiryDate: GenerateExpiryDate().toISOString(),
            ReferenceCode: referenceCode,
            UserEmail: account?.username || ""
        };
        try {
            const response = await fetch(shareEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // 'Authorization': `Bearer ${accessToken}`,
                    'X-Page-Url': window.location.href
                },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                throw new Error(`API call failed: ${response.status}`);
            }

            let result;
            const contentType = response.headers.get('content-type');
            const text = await response.text();

            if (!text) {
                result = null; // Blank response
            } else if (contentType && contentType.includes('application/json')) {
                try {
                    result = JSON.parse(text);
                } catch (err) {
                    result = text; // Not valid JSON, fallback to raw text
                }
            } else {
                result = text; // Non-JSON response
            }

            setLastInvitee(inviteeEmail); // Update lastInvitee
            setShowSuccess(true); // Show the success message bar
        } catch (error) {
            alert(`Share failed: ${error.message}`);
            setLastInvitee(""); // Update lastInvitee
        }
        finally {
            
            setReferenceCode(""); // Clear referenceCode
            setInviteeEmail("");
            setIsSharing(false);
        }
        
    };

    // Top-level header bar with page title from env variable
    const pageTitle = process.env.REACT_APP_PAGE_TITLE || "Appnme";

    if (inProgress === "login") {
        return (
            <>
                <div className="app-header-bar">{pageTitle}</div>
                <Stack>
                    <Stack.Item>
                        <span>Login is currently in progress!</span>
                    </Stack.Item>
                
                </Stack>
               
               
            </>
        );
    }

    if (!account) {
        return (
            <>
                <div className="app-header-bar">{pageTitle}</div>
                <div id="noUserMessage">
                <Stack>
                    <Stack.Item>
                        <span>There are currently no users signed in!</span><br />
                    </Stack.Item>
                    <Stack.Item><button id="signInButton" onClick={handleLogin}>Sign In</button></Stack.Item>
                    
                    </Stack>
                </div>
            </>
        );
    }
    if (accessToken && account && !userHasRequiredRole(account)) {
        return (
            <>
                <div className="app-header-bar">{pageTitle}</div>
                <div id="noUserMessage">
                    <Stack>
                        <Stack.Item>
                            <span>You do not have the required role ({REQUIRED_ROLE}) to access this application.</span><br />
                        </Stack.Item>
                    </Stack>
                </div>
            </>
        );
    }

    if (accessToken && account && userHasRequiredRole(account)) {
        return (
            <>
                <div className="app-header-bar">
                    {pageTitle}
                    <span id="signedInUser">Signed in as: {account.username}</span>
                </div>
                <div className="App">
                    <fluentAccordion>
                        <fluent-accordion-item id="instructions">
                            <span slot="heading">Instructions</span>
                            <div class="panel">
                                <ol>
                                    <li>Zip up your files into a single zip file. <br />Large files upto 10 GB should be possible, however we recommend smaller files for practical reasons</li>
                                    <li>Use the upload control below to select and upload the zip file to SharePoint. You can modify the name of the file in sharepoint before you upload.<br />
                                        this may take some time for large video content. Once complete, the control will show the SharePoint file name and URL .</li>
                                    <li>Once uploaded, enter the recipients email address and click the share button to send an invite to your recipient's email address. <br />This will assign read access to the file in sharepoint and then send a sharepoint invite to the user.<br /></li>
                                    <li>
                                        The default expiry date for shared files is <b>'{process.env.REACT_APP_DEFAULT_EXPIRY_DAYS}'</b> days from today.
                                    </li>
                                    <li>
                                        The SharePoint site in use is <b>'{process.env.REACT_APP_SP_SITE_DISPLAY_NAME}'</b> files can be found in the <b>{process.env.REACT_APP_SP_LIBRARY_NAME}</b> folder.
                                    </li>
                                    <li>Duplicate files will not be uploaded. If you try to upload a file with the same name as an existing file, you will receive an error message.</li>
                                    <li>If a file fails part-way through, it will be deleted from sharepoint to allow you to retry.</li>
                                    <li>The file can be shared with multiple emails by repeating the share action and entering a new email address each time. Make sure the file upload information is still displayed as you do this. .</li>
                                    <li>If the sharing process completes, you should get an acknowledgement message sent to your email.</li>

                                </ol>
                          
                                
                            </div>
                        </fluent-accordion-item>
                    </fluentAccordion>
                    <br/>
                    <LargeUpload ref={largeUploadRef} accessToken={accessToken} sharepointDriveId={process.env.REACT_APP_SP_DRIVE_ID} sharepointFolderId={process.env.REACT_APP_SP_FOLDER_ID} />
                    <div id="shareDiv" style={{ margin: "1em 0" }}>
                        <Stack>
                            <Stack.Item>  <h2>Sharing...</h2>
                            </Stack.Item>
                            {/* Spinner shown while sharing */}
                            {isSharing && (
                                <Stack.Item>
                                    <Spinner
                                        size={SpinnerSize.medium}
                                        label="Sharing in progress..."
                                        ariaLive="assertive"
                                    />
                                </Stack.Item>
                            )}
                            <Stack.Item>
                                <label htmlFor="inviteeEmail">Invitee email:</label>
                            </Stack.Item>
                            <Stack.Item>
                                <input
                                    id="inviteeEmail"
                                    type="email"
                                    maxLength={255}
                                    value={inviteeEmail}
                                    onChange={e => {
                                        setInviteeEmail(e.target.value);
                                        console.log("New email is :" + e.target.value);
                                    }}
                                />
                              
                                <br /><div hidden={isEmailValid} id="errorMsg" className="error-message">Email is required</div>
                            </Stack.Item>
                            <fluent-tooltip anchor="inviteeEmail">
                                The person who will receive a SharePoint invite to view or download the file
                            </fluent-tooltip>
                            <Stack.Item>
                                <label htmlFor="referenceCode">Reference code:</label>
                            </Stack.Item>
                            <Stack.Item>
                                <input
                                    id="referenceCode"
                                    type="text"
                                    maxLength={60}
                                    value={referenceCode}
                                    onChange={e => {
                                        // Only allow alphanumeric, spaces, and underscores
                                        const filtered = e.target.value.replace(/[^a-zA-Z0-9 _]/g, "");
                                        setReferenceCode(filtered);
                                        console.log("Reference code is :" + filtered);
                                    }}
                                />
                              
                                <br />
                            </Stack.Item>
                            <fluent-tooltip anchor="referenceCode">
                                This can be an internal reference, case number or other regulatory reference.
                            </fluent-tooltip>
                            <Stack.Item> </Stack.Item>
                            {showSuccess && <Stack.Item><SuccessExample /></Stack.Item>}
                            <Stack.Item> <button disabled={(!isEmailValid ||inviteeEmail.length <= 1 || largeUploadRef.current?.sharepointFilename.length <= 1)} id="sharebutton" onClick={onShareClicked}>Share</button>
                            </Stack.Item>
                           
                        </Stack>
                    </div>
                                 </div>
            </>
        );
    }

    // Waiting for token
    return (
        <>
            <div className="app-header-bar">{pageTitle}</div>
            <span>Authenticating...</span>
        </>
    );
}

export default App;
